/**
 * Warranty-fee reference table — the admin list where Engineering and
 * Accounting maintain the warranty fee schedule (reason + dollar amount, with
 * an obsolete flag). Reads through the shared ServerDataGrid and writes through
 * useGridCrud; the fee list is small, so rows are loaded once and filtered /
 * sorted in the grid.
 *
 * Contains:
 * - WarrantyFeeTable — the reference-data table
 */
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useUser } from '@sophocles/app';
import ServerDataGrid from '@sophocles/data-grids/components/ServerDataGrid';
import useGridCrud from '@sophocles/data-grids/datasource/useGridCrud';
import createReferenceCrudApi from '@sophocles/entities/referenceCrudApi';
import { addToast } from '@sophocles/features/toast';
import ReferenceCrudDialog from '@sophocles/views/admin/management/components/ReferenceCrudDialog';

const warrantyFeeApi = createReferenceCrudApi('/warrantyfees/');
const KNOWN_FIELDS = ['reason', 'fee'];
const FETCH_ALL = { limit: 10000 };

function WarrantyFeeTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const entityLabel = t('admin.entityLabels.warrantyFee', 'Warranty Fee');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null (closed), { entity } — entity null means create

  const refresh = useCallback(() => {
    setLoading(true);
    return warrantyFeeApi
      .list(FETCH_ALL)
      .then(data => setRows(data.results ?? []))
      .catch(() =>
        dispatch(
          addToast({
            message: t('admin.crudTable.loadError', 'Error while attempting to fetch {{entityLabel}}.', {
              entityLabel,
            }),
            level: 'error',
          }),
        ),
      )
      .finally(() => setLoading(false));
  }, [dispatch, t, entityLabel]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const crudGridState = useMemo(() => ({ refresh }), [refresh]);
  const { createRow, patchRow, isMutating, mutationError, resetError } = useGridCrud({
    gridState: crudGridState,
    create: warrantyFeeApi.create,
    patch: warrantyFeeApi.update,
    remove: warrantyFeeApi.remove,
    knownFields: KNOWN_FIELDS,
    messages: {
      created: t('admin.crudTable.saved', '{{entityLabel}} saved.', { entityLabel }),
      updated: t('admin.crudTable.saved', '{{entityLabel}} saved.', { entityLabel }),
    },
  });

  const { permissions } = useUser();
  const canEdit = permissions.isODLEngineering || permissions.isAccounting;

  const openCreate = useCallback(() => {
    resetError();
    setEditing({ entity: null });
  }, [resetError]);

  const openEdit = useCallback(
    row => {
      resetError();
      setEditing({ entity: row });
    },
    [resetError],
  );

  const closeDialog = useCallback(() => setEditing(null), []);

  const handleSubmit = useCallback(
    async values => {
      const { ok } = editing?.entity ? await patchRow(editing.entity.id, values) : await createRow(values);
      if (ok) closeDialog();
    },
    [editing, patchRow, createRow, closeDialog],
  );

  const columns = useMemo(() => {
    const cols = [
      { field: 'id', headerName: t('admin.crudTable.idColumn', 'ID'), width: 80 },
      { field: 'reason', headerName: t('admin.crudTable.reasonColumn', 'Reason'), flex: 1, minWidth: 120 },
      {
        field: 'fee',
        headerName: t('admin.crudTable.feeColumn', 'Fee'),
        width: 120,
        renderCell: ({ value }) => `$${value}`,
      },
      {
        field: 'obsolete',
        headerName: t('admin.crudTable.activeColumn', 'Active'),
        width: 90,
        renderCell: ({ value }) => (value ? t('general.no', 'No') : t('general.yes', 'Yes')),
      },
    ];
    if (canEdit) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <IconButton size="small" onClick={() => openEdit(row)}>
            <EditIcon fontSize="small" color="primary" />
          </IconButton>
        ),
      });
    }
    return cols;
  }, [t, canEdit, openEdit]);

  const fields = useMemo(
    () => [
      { name: 'reason', label: t('admin.crudTable.reasonColumn', 'Reason'), rows: 4 },
      { name: 'fee', label: t('admin.crudTable.feeColumn', 'Fee'), rows: 1 },
    ],
    [t],
  );

  const gridState = useMemo(() => ({ apiRef, loading, getRowId: row => row.id }), [apiRef, loading]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1 }}>
      <ServerDataGrid
        gridState={gridState}
        columns={columns}
        rows={rows}
        searchPlaceholder={t('admin.crudTable.search', 'Search {{entityLabel}}', { entityLabel })}
        createAction={
          canEdit
            ? {
                label: t('admin.crudTable.addNew', 'Add New {{entityLabel}}', { entityLabel }),
                onClick: openCreate,
              }
            : null
        }
      />
      {editing && (
        <ReferenceCrudDialog
          open
          entity={editing.entity}
          entityLabel={entityLabel}
          fields={fields}
          fieldErrors={mutationError.fieldErrors}
          isSubmitting={isMutating}
          onSubmit={handleSubmit}
          onClose={closeDialog}
        />
      )}
    </Box>
  );
}

export default WarrantyFeeTable;
